import { useQuery } from '@tanstack/react-query';
import { UserRound } from 'lucide-react';
import { signedUrlsFor } from '@/api/documents';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * Signed URLs for a set of photo documents, as one query. The key is the sorted id list, so a
 * grid re-rendering with the same farmers does not sign again; URLs last ten minutes and the
 * query refreshes before they expire.
 */
export function usePhotoUrls(documentIds: Array<string | null | undefined>) {
  const ids = [...new Set(documentIds.filter((id): id is string => Boolean(id)))].sort();
  return useQuery({
    queryKey: ['photo-urls', ids],
    queryFn: () => signedUrlsFor(ids),
    enabled: isSupabaseConfigured && ids.length > 0,
    staleTime: 8 * 60 * 1000,
  });
}

/**
 * A farmer's photograph, round, with a neutral placeholder when there is none - so a card
 * without a photo still lines up with one that has it, and says plainly that none was taken.
 */
export function FarmerPhoto({ url, name, size = 48 }: { url?: string | null; name?: string | null; size?: number }) {
  const style = { width: size, height: size };
  return url
    ? <img alt={name ? `Photograph of ${name}` : 'Farmer photograph'} className="farmer-photo" src={url} style={style} />
    : (
      <span aria-label="No photograph yet" className="farmer-photo farmer-photo-empty" role="img" style={style}>
        <UserRound size={Math.round(size * 0.5)} />
      </span>
    );
}
